import { writable, get, derived } from 'svelte/store';
import { apiService } from '../services/apiService.js'; 
import { searchEvidence, selectEvidence, deselectEvidence } from './evidenceStore.js'; // Replace fetchEvidenceByTopic with searchEvidence

// Create the base store
const createStore = () => {
  const { subscribe, set, update } = writable({
    selectedPromptId: null,
    selectedPromptDetails: null,
    sortedEvidenceDetails: [],
    sortedEvidenceIds: [],
    selectedEvidenceIds: [],
    evidence: [],
    prompts: [],
    savedPrompts: [],
    pagination: {
      currentPage: 1,
      itemsPerPage: 9, // Shows 3x3 grid
      totalPages: 1
    }
  });

  return {
    subscribe,
    set,
    update
  };
};

// Create the store instance
export const promptStore = createStore();

// Add derived store for bigQuestion
export const bigQuestion = derived(promptStore, $store => 
  $store.selectedPromptDetails?.bigQuestion || ''
);

// Function to validate prompts
function isValidPrompt(prompt) {
  return prompt && 
    typeof prompt === 'object' &&
    prompt._id &&
    prompt.bigQuestion &&
    prompt.promptScript &&
    Array.isArray(prompt.topics);
}

// Fetch prompts from API
export const fetchPrompts = async () => {
  try {
    const prompts = await apiService.getPrompts();
    promptStore.update(store => ({
      ...store,
      prompts,
      pagination: {
        ...store.pagination,
        totalPages: Math.ceil(prompts.length / store.pagination.itemsPerPage)
      }
    }));
  } catch (error) {
    console.error('Error fetching prompts:', error.message, error.stack);
  }
};

// Fetch prompt by ID
export const fetchPromptById = async (promptId) => {
  console.log('fetchPromptById called with ID:', promptId);
  try {
    const promptDetails = await apiService.getPromptDetails(promptId);
    
    promptStore.update(store => ({
      ...store,
      selectedPromptId: promptId,
      selectedPromptDetails: promptDetails
    }));

    // Extract the topic tag correctly from the topics array
    const topicTag = promptDetails.topics?.[0]?.topicTagStatic || 
                     promptDetails.topics?.[0]?.topicTag || 
                     (typeof promptDetails.topics?.[0] === 'string' ? promptDetails.topics[0] : null) ||
                     'general';

    // Search using the topic tag
    if (topicTag) {
      console.log('Searching with topic tag:', topicTag);
      await searchEvidence(topicTag);
    }

    return promptDetails;
  } catch (error) {
    console.error('Failed to fetch prompt by ID:', error);
    promptStore.update(store => ({
      ...store,
      selectedPromptId: null,
      selectedPromptDetails: null
    }));
    throw error;
  }
};

// Set selected prompt
export const setSelectedPrompt = async (promptId) => {
  try {
    promptStore.update(store => ({
      ...store,
      selectedPromptId: promptId
    }));

    const promptDetails = await apiService.getPromptDetails(promptId);
    
    promptStore.update(store => ({
      ...store,
      selectedPromptDetails: promptDetails
    }));

    // Extract the topic tag correctly from the topics array
    const topicTag = promptDetails.topics?.[0]?.topicTagStatic || 
                    promptDetails.topics?.[0]?.topicTag || 
                    (typeof promptDetails.topics?.[0] === 'string' ? promptDetails.topics[0] : null);

    // Only search for evidence if we have a valid topic tag
    if (topicTag && topicTag !== 'general') {
      try {
        console.log('Searching with topic tag:', topicTag);
        await searchEvidence(topicTag);
      } catch (error) {
        console.warn('Failed to search evidence, but continuing with prompt selection:', error);
        // Don't throw the error, just log it and continue
      }
    }

  } catch (error) {
    console.error('Failed to fetch prompt details:', error);
    promptStore.update(store => ({
      ...store,
      selectedPromptId: null,
      selectedPromptDetails: null
    }));
    throw error;
  }
};

// Update pagination
export const updatePagination = (totalItems, itemsPerPage) => {
  promptStore.update(store => ({
    ...store,
    pagination: {
      ...store.pagination,
      totalPages: Math.ceil(totalItems / itemsPerPage)
    }
  }));
};

// Save prompt to list
export const savePrompt = (prompt) => {
  // Validate prompt before saving
  if (!isValidPrompt(prompt)) {
    console.error('Invalid prompt format:', prompt);
    return;
  }

  promptStore.update(store => {
    const exists = store.savedPrompts.some(p => p._id === prompt._id);
    if (!exists) {
      // Ensure we only save the required fields
      const cleanPrompt = {
        _id: prompt._id,
        bigQuestion: prompt.bigQuestion,
        promptScript: prompt.promptScript,
        topics: prompt.topics
      };
      const updatedPrompts = [...store.savedPrompts, cleanPrompt];
      localStorage.setItem('savedPrompts', JSON.stringify(updatedPrompts));
      return {
        ...store,
        savedPrompts: updatedPrompts
      };
    }
    return store;
  });
};

// Remove saved prompt
export const removeSavedPrompt = (promptId) => {
  promptStore.update(store => {
    const updatedPrompts = store.savedPrompts.filter(p => p._id !== promptId);
    // Save to localStorage
    localStorage.setItem('savedPrompts', JSON.stringify(updatedPrompts));
    return {
      ...store,
      savedPrompts: updatedPrompts
    };
  });
};

// Load saved prompts from localStorage
export const loadSavedPrompts = () => {
  const saved = localStorage.getItem('savedPrompts');
  if (saved) {
    try {
      const parsedPrompts = JSON.parse(saved);
      // Validate each prompt
      const validPrompts = Array.isArray(parsedPrompts) 
        ? parsedPrompts.filter(isValidPrompt)
        : [];
      
      // If we found invalid prompts, update storage with only valid ones
      if (validPrompts.length !== parsedPrompts.length) {
        localStorage.setItem('savedPrompts', JSON.stringify(validPrompts));
      }

      promptStore.update(store => ({
        ...store,
        savedPrompts: validPrompts
      }));
    } catch (error) {
      console.error('Error loading saved prompts:', error);
      localStorage.removeItem('savedPrompts');
      promptStore.update(store => ({
        ...store,
        savedPrompts: []
      }));
    }
  }
};

// Debug check to ensure exported functions exist
console.log('promptStore has required exports:', {
  hasSetSelectedPrompt: typeof setSelectedPrompt === 'function',
  hasFetchPromptById: typeof fetchPromptById === 'function',
  hasSavePrompt: typeof savePrompt === 'function',
  hasRemoveSavedPrompt: typeof removeSavedPrompt === 'function',
  hasLoadSavedPrompts: typeof loadSavedPrompts === 'function'
});

// Initialize with test data
function setupTestingEnv(){
  promptStore.update(store => ({
    ...store,
    selectedPromptDetails: {
      "_id": "default_climate_prompt",
      "sourceURL": "https://www.pewresearch.org/science/2019/11/25/u-s-public-views-on-climate-and-energy/",
      "statistical_mentions": [
        "About two-thirds of U.S. adults (67%) say the federal government is doing too little to reduce the effects of climate change, and similar shares say the same about government efforts to protect air (67%) and water quality (68%) – findings that are consistent with results from a2018 Center survey. While there is strong consensus among Democrats (90%, including independents who lean to the Democratic Party) on the need for more government efforts to reduce the effects of climate change, Republican views are divided along ideological, generational and gender lines. A majority of moderate or liberal Republicans (65%, including GOP-leaning independents) say the federal government is doing too little to reduce the effects of climate change. In contrast, only about one-quarter of conservative Republicans (24%) say the same, while about half (48%) think the government is doing about the right amount and another 26% say it is doing too much."
      ],
      "article_context": "Majorities of Americans say the federal government is doing too little for key aspects of the environment, from protecting water or air quality to reducing the effects of climate change. And most believe the United States should focus on developing alternative sources of energy over expansion of fossil fuel sources, according to a new Pew Research Center survey.",
      "article_date": "2019",
      "bigQuestion": "How do people's political views affect what they think the government should do about climate change?",
      "promptScript": "Hey, did you know that a recent study from 2019 found that most Americans think the government needs to do more to fight climate change? But when you look at people's political views, things get really interesting. Most Democrats want the government to take stronger action, but Republicans are split. Younger and more moderate Republicans are more likely to say the government should do more, while older and more conservative Republicans often think the government is already doing enough or even too much. What do you think about that? Do you think people's political views really should play a big role in how the government addresses climate change?",
      "topics": [
        {
          "topicTag": "climate-energy-environment",
          "topicLabel": "Climate & Energy"
        }
      ],
      "variation_index": 0
    }
  }));
}

// Initialize testing environment
setupTestingEnv();

